import React from "react";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import mixpanel from "mixpanel-browser";

import {
  ChartCountry,
  COUNTRY_FLAGS,
  COUNTRY_NAME_KEYS,
} from "./heatwaveChartShared";

const colors = {
  textDark: "#0f2744",
  textMuted: "#5a7a94",
};

const CountrySwitch = styled.div`
  display: flex;
  justify-content: center;
  gap: 0.75rem;
  margin-bottom: 1.5rem;
  flex-wrap: wrap;
`;

const SwitchButton = styled.button<{ $active: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  border: 1px solid
    ${({ $active }) => ($active ? "rgba(74, 159, 212, 0.7)" : "rgba(126, 200, 227, 0.45)")};
  background: ${({ $active }) =>
    $active ? "rgba(255, 255, 255, 0.95)" : "rgba(255, 255, 255, 0.55)"};
  color: ${({ $active }) => ($active ? colors.textDark : colors.textMuted)};
  font-size: 1rem;
  font-weight: ${({ $active }) => ($active ? 600 : 500)};
  padding: 0.75rem 1.25rem;
  border-radius: 9999px;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: ${({ $active }) =>
    $active ? "0 2px 8px rgba(74, 159, 212, 0.2)" : "none"};
  line-height: 1.2;
  white-space: nowrap;

  &:hover {
    color: ${colors.textDark};
    border-color: rgba(74, 159, 212, 0.7);
  }
`;

const SwitchButtonFlag = styled.span`
  font-size: 1.375rem;
  line-height: 1;
`;

type HeatwaveCountryFilterProps = {
  country: ChartCountry;
  onChange: (country: ChartCountry) => void;
  availableCountries: ChartCountry[];
};

function getCountryLabel(code: ChartCountry, t: (key: string) => string): string {
  if (code === "ALL") return t("heatwave.perDegree.chartSwitch.all");
  return t(`heatwave.countryNames.${COUNTRY_NAME_KEYS[code]}`);
}

const HeatwaveCountryFilter: React.FC<HeatwaveCountryFilterProps> = ({
  country,
  onChange,
  availableCountries,
}) => {
  const { t } = useTranslation();

  return (
    <CountrySwitch>
      {availableCountries.map((code) => (
        <SwitchButton
          key={code}
          type="button"
          $active={country === code}
          onClick={() => { mixpanel.track("Heatwave Country Filter Changed", { country: code }); onChange(code); }}
          aria-pressed={country === code}
          aria-label={getCountryLabel(code, t)}
        >
          {code !== "ALL" && (
            <SwitchButtonFlag aria-hidden>{COUNTRY_FLAGS[code]}</SwitchButtonFlag>
          )}
          {getCountryLabel(code, t)}
        </SwitchButton>
      ))}
    </CountrySwitch>
  );
};

export default HeatwaveCountryFilter;
